import { Card } from "primereact/card";
import Highcharts from "./point.extensions";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { BsFillMicFill } from "react-icons/bs";
import ProjectService from "./ProjectService";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import Spinner from "../loader/Spinner";
import {
  calculateDate,
  getDecimalValue,
  getTimeZoneName,
} from "../../config/helpingFunctions";
import { Tooltip } from "react-tooltip";

var FileSaver = require("file-saver");

type VisualizationProps = {
  data: any;
};

const Visualization = (props: VisualizationProps) => {
  const { t } = useTranslation();
  HighchartsBoost(Highcharts);
  const chart1Ref = useRef<any>();
  const chart2Ref = useRef<any>();
  const chart3Ref = useRef<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [immutable, setImmutabel] = useState<boolean>(false);
  const [timeLevel, setTimeLevel] = useState<string>("Absolute Time");
  const [trace, setTrace] = useState<boolean>(true);
  const [norm, setNorm] = useState<boolean>(false);
  const [fft, setFFT] = useState<boolean>(false);
  const [vectorSum, setVectorSum] = useState<boolean>(false);
  const [propsValue, setPropsValue] = useState<any>([]);
  const [rowId, setRowId] = useState<any>();
  const [fileName, setFileName] = useState<any>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [componentRendered, setComponentRendered] = useState<number>(0);
  const [rightDisabled, setRightDisabled] = useState<boolean>(false);
  const [leftDisabled, setLeftDisabled] = useState<boolean>(false);

  const [triggerSelected, setTriggerSelected] = useState<boolean>(true);
  const [traces, setTraces] = useState<any>("Trace");
  const options = ["Trace", "Event"];
  const [xAxisUnit, setXAxisUnit] = useState<any>();
  const [yAxisUnit, setYAxisUnit] = useState<any>();
  const [zAxisUnit, setZAxisUnit] = useState<any>();

  const [deviceName, setDeviceName] = useState<string>("");
  const [deviceId, setDeviceId] = useState<string>("");
  const [gpsPos, setGpsPos] = useState<string>("");
  const [strtTime, setStrtTime] = useState<any>("");
  const [edTime, setEndTime] = useState<any>("");
  const [strTimeUTC, setStrtTimeUTC] = useState<any>();
  const [endTimeUTC, setEndTimeUTC] = useState<any>();

  const [maxX, setMaxX] = useState<number>();
  const [maxY, setMaxY] = useState<number>();
  const [maxZ, setMaxZ] = useState<number>();

  const [frequencyX, setFrequencyX] = useState<number>();
  const [frequencyY, setFrequencyY] = useState<number>();
  const [frequencyZ, setFrequencyZ] = useState<number>();

  const [triggerX, setTriggerX] = useState<number>(0);
  const [triggerY, setTriggerY] = useState<number>(0);
  const [triggerZ, setTriggerZ] = useState<number>(0);

  const [xData, setXdata] = useState<any>([]);
  const [relativeXData, setRelativeXData] = useState<any>([]);
  const [actualXData, setActualXData] = useState<any>([]);

  const [yData, setYdata] = useState<any>([]);
  const [relativeYData, setRelativeYData] = useState<any>([]);
  const [actualYData, setActualYData] = useState<any>([]);

  const [zData, setZdata] = useState<any>([]);
  const [relativeZData, setRelativeZData] = useState<any>([]);
  const [actualZData, setActualZData] = useState<any>([]);

  const [gpuToggle, setGpuToggle] = useState(false);

  const [xFFTData, setXFFTData] = useState<any>([]);
  const [yFFTData, setYFFTData] = useState<any>([]);
  const [zFFTData, setZFFTData] = useState<any>([]);

  const [xTrigger, setXTrigger] = useState<any>({});
  const [yTrigger, setYTrigger] = useState<any>({});
  const [zTrigger, setZTrigger] = useState<any>({});
  const [vectoSumTrigger, setVectorSumTrigger] = useState<any>({});

  const [dinXData, setDinXData] = useState<any>([]);
  const [dinYData, setDinYData] = useState<any>([]);
  const [dinZData, setDinZData] = useState<any>([]);

  const [xAxisVal, setXAxisVal] = useState<any>({});

  const [yAxisVal, setYAxisVal] = useState<any>({});

  const [zAxisVal, setZAxisVal] = useState<any>({});

  const [timeOffset, setTimeOffset] = useState<any>();
  const [vectorSumData, setVectorSumData] = useState<any>([]);

  const [vectorConfig, setVectorConfig] = useState<any>({
    title: {
      text: "",
    },
  });

  const [xConfig, setxConfig] = useState<any>({
    title: {
      text: "",
    },
  });
  const [yConfig, setyConfig] = useState<any>({
    title: {
      text: "",
    },
  });
  const [zConfig, setzConfig] = useState<any>({
    title: {
      text: `${t("relative_timein")} [s]`,
    },
  });

  const projectService = new ProjectService();

  const graphHeight = (window.innerHeight - 50) / 3;

  const graph_x = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: gpuToggle,
      // Chart-level boost when there are more than 5 series in the chart
      usePreAllocated: true,
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    chart: {
      zoomType: "x",
      height: 250,
      pixelRatio: window.devicePixelRatio || 1,
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      labels: { enabled: false },
      events: {
        afterSetExtremes: function (event: any) {
          chart2Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      ...JSON.parse(JSON.stringify(xConfig)),
    },
    yAxis: {
      title: {
        text: xAxisUnit,
      },
      ...JSON.parse(JSON.stringify(xAxisVal)),
      ...JSON.parse(JSON.stringify(xTrigger)),
    },
    title: {
      text: "X-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: xData,
        showInLegend: false,
        name: "Amplitude mm/s ",
        lineWidth: 2,
      },
    ],
  };

  const graph_y = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: gpuToggle,
      // Chart-level boost when there are more than 5 series in the chart
      usePreAllocated: true,
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    chart: {
      zoomType: "x",
      height: 250,
    },

    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      labels: { enabled: false },
      events: {
        afterSetExtremes: function (event: any) {
          chart1Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      ...JSON.parse(JSON.stringify(yConfig)),
    },
    yAxis: {
      title: {
        text: yAxisUnit,
      },
      ...JSON.parse(JSON.stringify(yAxisVal)),
      ...JSON.parse(JSON.stringify(yTrigger)),
    },
    title: {
      text: "Y-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: yData,
        showInLegend: false,
        name: "Amplitude mm/s ",
        lineWidth: 2,
      },
    ],
  };

  const graph_z = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: gpuToggle,
      // Chart-level boost when there are more than 5 series in the chart
      usePreAllocated: true,
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    chart: {
      zoomType: "x",
      height: 250,
    },

    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      events: {
        afterSetExtremes: function (event: any) {
          chart1Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      ...JSON.parse(JSON.stringify(zConfig)),
    },
    yAxis: {
      title: {
        text: zAxisUnit,
      },
      ...JSON.parse(JSON.stringify(zAxisVal)),
      ...JSON.parse(JSON.stringify(zTrigger)),
    },
    title: {
      text: "Z-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: zData,
        showInLegend: false,
        name: "Amplitude mm/s ",
        lineWidth: 2,
      },
    ],
  };

  const scatter_graph = {
    chart: {
      type: "scatter",
      zoomType: "xy",
      height: 550,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "DIN PLOT GRAPH",
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      title: {
        enabled: true,
        text: "Frequency [Hz]",
      },
      max: 50,
      min: 0,
    },
    yAxis: {
      title: {
        text: "Velocity [mm/s]",
      },
      max: 50,
      min: 0,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          states: {
            hover: {
              enabled: false,
              lineColor: "rgb(100,100,100)",
            },
          },
        },

        tooltip: {
          headerFormat: "<b>{series.name}</b><br>",
          pointFormat: "{point.x} mm/s, {point.y} Hz",
        },
      },
    },
    series: [
      // ...JSON.parse(JSON.stringify(dinConfig)),
      {
        type: "line",
        name: "1",
        data: [
          [0, 20],
          [10, 20],
          [50, 40],
          [200, 50],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        type: "line",
        name: "2",
        color: "rgba(252, 144, 3)",
        data: [
          [0, 5],
          [10, 5],
          [50, 15],
          [100, 20],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        type: "line",
        name: "3",
        color: "rgba(252, 186, 3)",
        data: [
          [0, 3],
          [10, 3],
          [50, 8],
          [100, 10],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        name: "K1(X)",
        color: "rgba(235, 64, 52)",
        data: dinXData,
      },
      {
        name: "K2(Y)",
        color: "rgba(52, 70, 235)",
        data: dinYData,
      },
      {
        name: "K3(Z)",
        color: "rgba(235, 52, 104)",
        data: dinZData,
      },
    ],
  };

  const fftGraph_x = {
    chart: {
      zoomType: "x",
      height: 250,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "X-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    tooltip: {
      valueDecimals: 2,
    },

    xAxis: {
      crosshair: true,
      labels: { enabled: false },
      events: {
        afterSetExtremes: function (event: any) {
          chart2Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      title: {
        text: "",
      },
    },

    yAxis: {
      title: {
        text: "Intentsität",
      },
    },

    series: [
      {
        data: xFFTData,
        showInLegend: false,
        name: "Amplitude",
      },
    ],
  };

  const fftGraph_y = {
    chart: {
      zoomType: "x",
      height: 250,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Y-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      labels: { enabled: false },
      events: {
        afterSetExtremes: function (event: any) {
          chart1Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      title: {
        text: "",
      },
    },

    yAxis: {
      title: {
        text: "Intentsität",
      },
    },
    series: [
      {
        data: yFFTData,
        showInLegend: false,
        name: "Amplitude",
      },
    ],
  };

  const fftGraph_z = {
    chart: {
      zoomType: "x",
      height: 250,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Z-Axis Graph",
    },

    subtitle: {
      text: "",
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      events: {
        afterSetExtremes: function (event: any) {
          chart1Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
          chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
        },
      },
      title: {
        text: "Frequency [Hz]",
      },
    },

    yAxis: {
      title: {
        text: "Intentsität",
      },
    },
    series: [
      {
        data: zFFTData,
        showInLegend: false,
        name: "Amplitude",
      },
    ],
  };

  const vector_sum_graph = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: gpuToggle,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },
    plotOptions: {
      series: {
        boostThreshold: 2000, // Set the threshold for boosting
      },
    },
    chart: {
      zoomType: "x",
      height: graphHeight,
    },

    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      ...JSON.parse(JSON.stringify(vectorConfig)),
    },
    yAxis: {
      title: {
        text: zAxisUnit,
      },
      ...JSON.parse(JSON.stringify(vectoSumTrigger)),
    },
    title: {
      text: "Vector Sum Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: vectorSumData,
        showInLegend: false,
        name: "Amplitude mm/s ",
      },
    ],
  };

  const downloadSingleSeg2 = () => {
    projectService.getSingleSeg2(props.data).then((response) => {
      FileSaver.saveAs(response.url);
    });
  };

  const selectTime = () => {
    setDisabled(false);
    if (timeLevel === "Absolute Time") {
      setTimeLevel("Relative Time");
      setGpuToggle(false);
      setImmutabel(true);
      setxConfig({
        title: {
          text: "",
        },
        type: "datetime",
        label: {
          format: "%H:%M:%S.%L",
        },
      });
      setyConfig({
        title: {
          text: "",
        },
        type: "datetime",
        label: {
          format: "%H:%M:%S.%L",
        },
      });
      setzConfig({
        title: {
          text: `${t("relative_timein")} [HH:MM:SS:MS]`,
        },
        type: "datetime",
        label: {
          format: "%H:%M:%S.%L",
        },
      });
      if (relativeXData.length > 0) {
        setXdata(relativeXData);
        setImmutabel(true);
      }
      if (relativeYData.length > 0) {
        setYdata(relativeYData);
        setImmutabel(true);
      }
      if (relativeZData.length > 0) {
        setZdata(relativeZData);
        setImmutabel(true);
      }
      projectService
        .getVisualizationData(
          deviceId,
          parseInt(strTimeUTC),
          parseInt(endTimeUTC)
        )
        .then((response) => {
          let x_data: any;
          let y_data: any;
          let z_data: any;
          /**  FOR DATETIME **/
          response.map((data: any) => {
            if (data["channel_X"]) {
              setXAxisUnit(data.unit);
              let startTime: any = "";
              if (localStorage.getItem("timezone") !== "UTC") {
                var date = new Date(data.strt_time * 1000);
                date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                startTime = new Date(date.toISOString()).getTime();
              } else {
                startTime = new Date(data.start_time).getTime();
              }
              x_data = data.channel_X.map((x: any, i: any) => [
                startTime +
                  (data.time_interval / data.channel_X.length) * i * 1000,
                x,
              ]);
            } else if (data["channel_Y"]) {
              setYAxisUnit(data.unit);
              let startTime: any = "";
              if (localStorage.getItem("timezone") !== "UTC") {
                var date = new Date(data.strt_time * 1000);
                date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                startTime = new Date(date.toISOString()).getTime();
              } else {
                startTime = new Date(data.start_time).getTime();
              }
              y_data = data.channel_Y.map((x: any, i: any) => [
                startTime +
                  (data.time_interval / data.channel_Y.length) * i * 1000,
                x,
              ]);
            } else if (data["channel_Z"]) {
              setZAxisUnit(data.unit);
              let startTime: any = "";
              if (localStorage.getItem("timezone") !== "UTC") {
                var date = new Date(Number(data.strt_time) * 1000);
                date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                startTime = new Date(date.toISOString()).getTime();
              } else {
                startTime = new Date(Number(data.start_time)).getTime();
              }
              z_data = data.channel_Z.map((x: any, i: any) => [
                startTime +
                  (data.time_interval / data.channel_Z.length) * i * 1000,
                x,
              ]);
            }
          });
          if (JSON.stringify(relativeXData) !== JSON.stringify(x_data)) {
            setXdata(x_data);
            setImmutabel(true);
            setRelativeXData(x_data);
          }
          if (JSON.stringify(relativeYData) !== JSON.stringify(y_data)) {
            setYdata(y_data);
            setImmutabel(true);
            setRelativeYData(y_data);
          }
          if (JSON.stringify(relativeZData) !== JSON.stringify(z_data)) {
            setZdata(z_data);
            setImmutabel(true);
            setRelativeZData(z_data);
          }
        });

      if (vectorSum) {
        setVectorConfig({
          title: {
            text: `${t("relative_timein")} [HH:MM:SS:MS]`,
          },
          type: "datetime",
          label: {
            format: "%H:%M:%S.%L",
          },
        });
        projectService
          .getVectorSumData(
            propsValue["station"],
            propsValue["start_time_utc"],
            propsValue["end_time_utc"]
          )
          .then((response: any) => {
            let data: any;
            let startTime: any = "";
            if (localStorage.getItem("timezone") !== "UTC") {
              var date = new Date(response.strt_time * 1000);
              date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
              startTime = new Date(date.toISOString()).getTime();
            } else {
              startTime = new Date(response.start_time).getTime();
            }
            data = response.vector_sum.map((x: any, i: any) => [
              startTime +
                (response.time_interval / response.vector_sum.length) *
                  i *
                  1000,
              x,
            ]);

            setVectorSumData(data);
          });
      }
    } else if (timeLevel === "Relative Time") {
      setTimeLevel("Absolute Time");
      setGpuToggle(true);
      setxConfig({
        title: {
          text: "",
        },
      });
      setyConfig({
        title: {
          text: "",
        },
      });
      setzConfig({
        title: {
          text: `${t("relative_timein")} [s]`,
        },
      });
      if (actualXData.length > 0) {
        setXdata(actualXData);
        setImmutabel(true);
      }
      if (actualYData.length > 0) {
        setYdata(actualYData);
        setImmutabel(true);
      }
      if (actualZData.length > 0) {
        setZdata(actualZData);
        setImmutabel(true);
      }
      projectService
        .getVisualizationData(
          deviceId,
          parseInt(strTimeUTC),
          parseInt(endTimeUTC)
        )
        .then((response) => {
          let x_data: any;
          let y_data: any;
          let z_data: any;
          /**  FOR TIME SECONDS **/
          response.map((data: any) => {
            if (data["channel_X"]) {
              setXAxisUnit(data.unit);
              x_data = data.channel_X.map((x: any, i: any) => [
                (data.time_interval / data.channel_X.length) * i,
                x,
              ]);
            } else if (data["channel_Y"]) {
              setYAxisUnit(data.unit);
              y_data = data.channel_Y.map((x: any, i: any) => [
                (data.time_interval / data.channel_Y.length) * i,
                x,
              ]);
            } else if (data["channel_Z"]) {
              setZAxisUnit(data.unit);
              z_data = data.channel_Z.map((x: any, i: any) => [
                (data.time_interval / data.channel_Z.length) * i,
                x,
              ]);
            }
          });
          if (JSON.stringify(actualXData) !== JSON.stringify(x_data)) {
            setXdata(x_data);
            setImmutabel(true);
            setActualXData(x_data);
          }
          if (JSON.stringify(actualYData) !== JSON.stringify(y_data)) {
            setYdata(y_data);
            setImmutabel(true);
            setActualYData(y_data);
          }
          if (JSON.stringify(actualZData) !== JSON.stringify(z_data)) {
            setZdata(z_data);
            setImmutabel(true);
            setActualZData(z_data);
          }
        });

      if (vectorSum) {
        setVectorConfig({
          title: {
            text: `${t("relative_timein")} [s]`,
          },
        });
        projectService
          .getVectorSumData(
            propsValue["station"],
            propsValue["start_time_utc"],
            propsValue["end_time_utc"]
          )
          .then((response: any) => {
            let data: any;
            data = response.vector_sum.map((x: any, i: any) => [
              (response.time_interval / response.vector_sum.length) * i,
              x,
            ]);

            setVectorSumData(data);
          });
      }
    }
  };

  const triggerApply = () => {
    if (!triggerSelected) {
      setTriggerSelected(true);
    } else {
      setTriggerSelected(false);
    }

    if (triggerSelected) {
      setXTrigger({
        max: triggerX + 0.5,
        min: -triggerX - 0.5,
        plotLines: [
          {
            value: triggerX,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
          {
            value: triggerX * -1,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
        ],
      });

      setYTrigger({
        max: triggerY + 0.5,
        min: -triggerY - 0.5,
        plotLines: [
          {
            value: triggerY,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
          {
            value: triggerY * -1,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
        ],
      });

      setZTrigger({
        max: triggerZ + 0.5,
        min: -triggerZ - 0.5,
        plotLines: [
          {
            value: triggerZ,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
          {
            value: triggerZ * -1,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
        ],
      });

      if (vectorSum) {
        setVectorSumTrigger({
          max: triggerZ + 0.5,
          min: -triggerZ - 0.5,
          plotLines: [
            {
              value: triggerZ,
              color: "green",
              dashStyle: "shortdash",
              width: 2,
            },
            {
              value: triggerZ * -1,
              color: "red",
              dashStyle: "shortdash",
              width: 2,
            },
          ],
        });
      }
    } else {
      setImmutabel(true);
      setXTrigger({});
      setYTrigger({});
      setZTrigger({});
      setVectorSumTrigger({});
    }
  };

  const normSelect = () => {
    setIsLoading(true);
    setTrace(false);
    setDisabled(true);
    setNorm(true);
    setFFT(false);
    setVectorSum(false);
    projectService
      .getDinData(
        propsValue["station"],
        propsValue["start_time_utc"],
        propsValue["end_time_utc"]
      )
      .then((response) => {
        let x_data: any;
        let y_data: any;
        let z_data: any;
        x_data = [Math.round(response["max_X"] * 100) / 100, frequencyX];
        y_data = [Math.round(response["max_Y"] * 100) / 100, frequencyY];
        z_data = [Math.round(response["max_Z"] * 100) / 100, frequencyZ];

        setDinXData(x_data);
        setDinYData(y_data);
        setDinZData(z_data);
        setIsLoading(false);
      });
  };

  const fftSelect = () => {
    setIsLoading(true);
    setDisabled(true);
    setTrace(false);
    setNorm(false);
    setFFT(true);
    setVectorSum(false);
    projectService
      .getFFTData(
        propsValue["station"],
        propsValue["start_time_utc"],
        propsValue["end_time_utc"]
      )
      .then((response) => {
        let x_data: any;
        let y_data: any;
        let z_data: any;
        let channelX_length = response.channel_X.length * 2;
        let channelY_length = response.channel_Y.length * 2;
        let channelZ_length = response.channel_Z.length * 2;

        const x_data_samplin_rate =
          response["sampling_rate"] / 2 / response.channel_X.length;
        x_data = response.channel_X.map((x: any, i: any) => [
          +(x_data_samplin_rate * i).toFixed(3),
          (x / channelX_length) * 2,
        ]);

        const y_data_samplin_rate =
          response["sampling_rate"] / 2 / response.channel_Y.length;
        y_data = response.channel_Y.map((x: any, i: any) => [
          +(y_data_samplin_rate * i).toFixed(3),
          (x / channelY_length) * 2,
        ]);

        const z_data_samplin_rate =
          response["sampling_rate"] / 2 / response.channel_Z.length;
        z_data = response.channel_Z.map((x: any, i: any) => [
          +(z_data_samplin_rate * i).toFixed(3),
          (x / channelZ_length) * 2,
        ]);

        setXFFTData(x_data);
        setYFFTData(y_data);
        setZFFTData(z_data);
        setIsLoading(false);
        setComponentRendered(componentRendered + 1);
      });
  };

  const vectorSumSelect = () => {
    setTrace(true);
    setNorm(false);
    setFFT(false);
    setVectorSum(true);
    setComponentRendered(componentRendered + 1);

    if (!triggerSelected) {
      setVectorSumTrigger({
        max: triggerZ + 0.5,
        min: -triggerZ - 0.5,
        plotLines: [
          {
            value: triggerZ,
            color: "green",
            dashStyle: "shortdash",
            width: 2,
          },
          {
            value: triggerZ * -1,
            color: "red",
            dashStyle: "shortdash",
            width: 2,
          },
        ],
      });
    }

    if (timeLevel === "Relative Time") {
      setVectorConfig({
        title: {
          text: `${t("relative_timein")} [HH:MM:SS:MS]`,
        },
        type: "datetime",
        label: {
          format: "%H:%M:%S.%L",
        },
      });
      projectService
        .getVectorSumData(
          propsValue["station"],
          propsValue["start_time_utc"],
          propsValue["end_time_utc"]
        )
        .then((response: any) => {
          let startTime: any = "";
          if (localStorage.getItem("timezone") !== "UTC") {
            var date = new Date(response.strt_time * 1000);
            date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
            startTime = new Date(date.toISOString()).getTime();
          } else {
            startTime = new Date(response.strt_time).getTime();
          }
          let data: any;
          data = response.vector_sum.map((x: any, i: any) => [
            startTime +
              (response.time_interval / response.vector_sum.length) * i * 1000,
            x,
          ]);

          setVectorSumData(data);
        });
    } else if (timeLevel === "Absolute Time") {
      setVectorConfig({
        title: {
          text: `${t("relative_timein")} [s]`,
        },
      });
      projectService
        .getVectorSumData(
          propsValue["station"],
          propsValue["start_time_utc"],
          propsValue["end_time_utc"]
        )
        .then((response: any) => {
          let data: any;
          data = response.vector_sum.map((x: any, i: any) => [
            (response.time_interval / response.vector_sum.length) * i,
            x,
          ]);

          setVectorSumData(data);
        });
    }
    if (vectorSum) {
      setVectorSum(false);
      setDisabled(false);
    }
  };

  const configureDecimal = (val: any, fixedVal: number) => {
    return localStorage.getItem("lang") === "en"
      ? Number(val).toFixed(fixedVal)
      : Number(val).toFixed(fixedVal).toString().replace(/\./g, ",");
  };

  const onNextSelect = () => {
    setIsLoading(true);
    setLeftDisabled(false);
    projectService
      .getVisualizationFilter(deviceId, rowId, "next")
      .then((response) => {
        let apiData: any = {};
        if (Object.keys(response).length != 0) {
          response.map((data: any) => {
            if (data["channel_X"]) {
              apiData = {
                station: data["station"],
                start_time_utc: data["start_time_utc"],
                end_time_utc: data["end_time_utc"],
              };
              setPropsValue({
                station: data["station"],
                start_time_utc: data["start_time_utc"],
                end_time_utc: data["end_time_utc"],
              });
            }
          });
          projectService
            .getVisualizationData(
              apiData["station"],
              apiData["start_time_utc"],
              apiData["end_time_utc"]
            )
            .then((response) => {
              response.map((data: any) => {
                if (data["channel_X"]) {
                  setRowId(data.rowId);
                  setDeviceName(data.header_data["device_name"]);
                  const date = calculateDate(
                    new Date(data.start_time).getTime() / 1000
                  );
                  const eddate = calculateDate(
                    new Date(data.end_time).getTime() / 1000
                  );
                  setStrtTime(date);
                  setEndTime(eddate);

                  setStrtTimeUTC(Date.parse(data.start_time) / 1000);
                  setEndTimeUTC(Date.parse(data.end_time) / 1000);
                  setDeviceId(data.did);
                  const pos: any = getDecimalValue(data.gps_pos);
                  setGpsPos(pos);
                  setXAxisUnit(data.unit);
                  // setMaxX(data.max.toFixed(3));
                  setTriggerX(parseFloat(data.trigger_level));
                }
              });
            });

          if (norm) {
            normSelect();
            setIsLoading(false);
          }

          if (fft) {
            fftSelect();
            setIsLoading(false);
          }
          if (trace) {
            setTrace(true);
            setFFT(false);
            setNorm(false);
            setVectorSum(false);
            if (timeLevel === "Absolute Time") {
              projectService
                .getVisualizationData(
                  response[0]["did"],
                  response[0]["start_time_utc"],
                  response[0]["end_time_utc"]
                )
                .then((response) => {
                  let x_data: any;
                  let y_data: any;
                  let z_data: any;

                  /**  FOR TIME SECONDS **/
                  response.map((data: any) => {
                    if (data["channel_X"]) {
                      setRowId(data.rowId);
                      setDeviceName(data.header_data["device_name"]);
                      const date = calculateDate(
                        new Date(data.start_time).getTime() / 1000
                      );
                      const eddate = calculateDate(
                        new Date(data.end_time).getTime() / 1000
                      );
                      setStrtTime(date);
                      setEndTime(eddate);
                      setStrtTimeUTC(Date.parse(data.start_time) / 1000);
                      setEndTimeUTC(Date.parse(data.end_time) / 1000);
                      setDeviceId(data.did);
                      const pos: any = getDecimalValue(data.gps_pos);
                      setGpsPos(pos);
                      setXAxisUnit(data.unit);
                      const max_X: any = configureDecimal(data.max, 3);
                      setMaxX(max_X);
                      const freq_X: any = configureDecimal(data.frequence_X, 2);
                      setFrequencyX(freq_X);
                      setTriggerX(parseFloat(data.trigger_level));
                      x_data = data.channel_X.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_X.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    } else if (data["channel_Y"]) {
                      setYAxisUnit(data.unit);
                      const max_Y: any = configureDecimal(data.max, 3);
                      setMaxY(max_Y);
                      const freq_Y: any = configureDecimal(data.frequence_Y, 2);
                      setFrequencyX(freq_Y);
                      setTriggerY(parseFloat(data.trigger_level));
                      y_data = data.channel_Y.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_Y.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    } else if (data["channel_Z"]) {
                      setZAxisUnit(data.unit);
                      const max_Z: any = configureDecimal(data.max, 3);
                      setMaxZ(max_Z);
                      const freq_Z: any = configureDecimal(data.frequence_Z, 2);
                      setFrequencyX(freq_Z);
                      setTriggerZ(parseFloat(data.trigger_level));
                      z_data = data.channel_Z.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_Z.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    }
                  });
                  setXdata(x_data);
                  setYdata(y_data);
                  setZdata(z_data);
                  setImmutabel(true);
                  setIsLoading(false);
                  setComponentRendered(componentRendered + 1);
                });
            } else if (timeLevel === "Relative Time") {
              setxConfig({
                title: {
                  text: "",
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              setyConfig({
                title: {
                  text: "",
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              setzConfig({
                title: {
                  text: `${t("relative_timein")} [HH:MM:SS:MS]`,
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              projectService
                .getVisualizationData(
                  response[0]["did"],
                  response[0]["start_time_utc"],
                  response[0]["end_time_utc"]
                )
                .then((response) => {
                  let x_data: any;
                  let y_data: any;
                  let z_data: any;

                  /**  FOR TIME SECONDS **/
                  response.map((data: any) => {
                    if (data["channel_X"]) {
                      setXAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      x_data = data.channel_X.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_X.length) *
                            i *
                            1000,
                        x,
                      ]);
                    } else if (data["channel_Y"]) {
                      setYAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      y_data = data.channel_Y.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_Y.length) *
                            i *
                            1000,
                        x,
                      ]);
                    } else if (data["channel_Z"]) {
                      setZAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      z_data = data.channel_Z.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_Z.length) *
                            i *
                            1000,
                        x,
                      ]);
                    }
                  });
                  setXdata(x_data);
                  setYdata(y_data);
                  setZdata(z_data);
                  setImmutabel(true);
                  setIsLoading(false);
                  setComponentRendered(componentRendered + 1);
                });
            }
          }
        } else {
          setRightDisabled(true);
          setIsLoading(false);
        }
      });
  };

  const onPreviousSelect = () => {
    setIsLoading(true);
    setRightDisabled(false);
    projectService
      .getVisualizationFilter(deviceId, rowId, "previous")
      .then((response) => {
        let apiData: any = {};
        if (Object.keys(response).length !== 0) {
          response.map((data: any) => {
            if (data["channel_X"]) {
              apiData = {
                station: data["station"],
                start_time_utc: data["start_time_utc"],
                end_time_utc: data["end_time_utc"],
              };
              setPropsValue({
                station: data["station"],
                start_time_utc: data["start_time_utc"],
                end_time_utc: data["end_time_utc"],
              });
            }
          });

          projectService
            .getVisualizationData(
              apiData["station"],
              apiData["start_time_utc"],
              apiData["end_time_utc"]
            )
            .then((response) => {
              response.map((data: any) => {
                if (data["channel_X"]) {
                  setRowId(data.rowId);
                  setDeviceName(data.header_data["device_name"]);
                  const date = calculateDate(
                    new Date(data.start_time).getTime() / 1000
                  );
                  const eddate = calculateDate(
                    new Date(data.end_time).getTime() / 1000
                  );
                  setStrtTime(date);
                  setEndTime(eddate);
                  setStrtTimeUTC(Date.parse(data.start_time) / 1000);
                  setEndTimeUTC(Date.parse(data.end_time) / 1000);
                  setDeviceId(data.did);
                  const pos: any = getDecimalValue(data.gps_pos);
                  setGpsPos(pos);
                  setXAxisUnit(data.unit);
                  // setMaxX(data.max.toFixed(3));
                  setTriggerX(parseFloat(data.trigger_level));
                }
              });
            });

          if (norm) {
            normSelect();
            setIsLoading(false);
          }

          if (fft) {
            fftSelect();
            setIsLoading(false);
          }
          if (trace) {
            setTrace(true);
            setFFT(false);
            setNorm(false);
            setVectorSum(false);
            if (timeLevel === "Absolute Time") {
              projectService
                .getVisualizationData(
                  response[0]["did"],
                  response[0]["start_time_utc"],
                  response[0]["end_time_utc"]
                )
                .then((response) => {
                  let x_data: any;
                  let y_data: any;
                  let z_data: any;

                  /**  FOR TIME SECONDS **/
                  response.map((data: any) => {
                    if (data["channel_X"]) {
                      setRowId(data.rowId);
                      setDeviceName(data.header_data["device_name"]);
                      const date = calculateDate(
                        new Date(data.start_time).getTime() / 1000
                      );
                      const eddate = calculateDate(
                        new Date(data.end_time).getTime() / 1000
                      );
                      setStrtTime(date);
                      setEndTime(eddate);
                      setStrtTimeUTC(Date.parse(data.start_time) / 1000);
                      setEndTimeUTC(Date.parse(data.end_time) / 1000);
                      setDeviceId(data.did);
                      const pos: any = getDecimalValue(data.gps_pos);
                      setGpsPos(pos);
                      setXAxisUnit(data.unit);
                      const max_X: any = configureDecimal(data.max, 3);
                      setMaxX(max_X);
                      const freq_X: any = configureDecimal(data.frequence_X, 2);
                      setFrequencyX(freq_X);
                      setTriggerX(parseFloat(data.trigger_level));
                      x_data = data.channel_X.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_X.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    } else if (data["channel_Y"]) {
                      setYAxisUnit(data.unit);
                      const max_Y: any = configureDecimal(data.max, 3);
                      setMaxY(max_Y);
                      const freq_Y: any = configureDecimal(data.frequence_Y, 2);
                      setFrequencyY(freq_Y);
                      setTriggerY(parseFloat(data.trigger_level));
                      y_data = data.channel_Y.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_Y.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    } else if (data["channel_Z"]) {
                      setZAxisUnit(data.unit);
                      const max_Z: any = configureDecimal(data.max, 3);
                      setMaxZ(max_Z);
                      const freq_Z: any = configureDecimal(data.frequence_Z, 2);
                      setFrequencyZ(freq_Z);
                      setTriggerZ(parseFloat(data.trigger_level));
                      z_data = data.channel_Z.map((x: any, i: any) => [
                        +(
                          (data.time_interval / data.channel_Z.length) *
                          i
                        ).toFixed(3),
                        x,
                      ]);
                    }
                  });
                  setImmutabel(true);
                  setXdata(x_data);
                  setYdata(y_data);
                  setZdata(z_data);
                  setIsLoading(false);
                  setComponentRendered(componentRendered + 1);
                });
            } else if (timeLevel === "Relative Time") {
              setxConfig({
                title: {
                  text: "",
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              setyConfig({
                title: {
                  text: "",
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              setzConfig({
                title: {
                  text: `${t("relative_timein")} [HH:MM:SS:MS]`,
                },
                type: "datetime",
                label: {
                  format: "%H:%M:%S.%L",
                },
              });
              projectService
                .getVisualizationData(
                  response[0]["did"],
                  response[0]["start_time_utc"],
                  response[0]["end_time_utc"]
                )
                .then((response) => {
                  let x_data: any;
                  let y_data: any;
                  let z_data: any;

                  /**  FOR TIME SECONDS **/
                  response.map((data: any) => {
                    if (data["channel_X"]) {
                      setXAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      x_data = data.channel_X.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_X.length) *
                            i *
                            1000,
                        x,
                      ]);
                    } else if (data["channel_Y"]) {
                      setYAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      y_data = data.channel_Y.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_Y.length) *
                            i *
                            1000,
                        x,
                      ]);
                    } else if (data["channel_Z"]) {
                      setZAxisUnit(data.unit);
                      let startTime: any = "";
                      if (localStorage.getItem("timezone") !== "UTC") {
                        var date = new Date(data.strt_time * 1000);
                        date.setMinutes(date.getMinutes() + timeOffset[1] * 60);
                        startTime = new Date(date.toISOString()).getTime();
                      } else {
                        startTime = new Date(data.start_time).getTime();
                      }
                      z_data = data.channel_Z.map((x: any, i: any) => [
                        startTime +
                          (data.time_interval / data.channel_Z.length) *
                            i *
                            1000,
                        x,
                      ]);
                    }
                  });
                  setImmutabel(true);
                  setXdata(x_data);
                  setYdata(y_data);
                  setZdata(z_data);
                  setIsLoading(false);
                  setComponentRendered(componentRendered + 1);
                });
            }
          }
        } else {
          setLeftDisabled(true);
          setIsLoading(false);
        }
      });
  };

  const selectTraceEvent = (e: any) => {
    setTraces(e.value);
    if (e.value === "Event") {
      let maxArr: any = [
        chart1Ref?.current?.chart.yAxis[0]["max"],
        chart2Ref?.current?.chart.yAxis[0]["max"],
        chart3Ref?.current?.chart.yAxis[0]["max"],
      ];

      let minArr: any = [
        chart1Ref?.current?.chart.yAxis[0]["min"],
        chart2Ref?.current?.chart.yAxis[0]["min"],
        chart3Ref?.current?.chart.yAxis[0]["min"],
      ];

      let graphMax = Math.max.apply(null, maxArr);
      let graphMin = Math.min.apply(null, minArr);

      setXAxisVal({
        max: graphMax,
        min: graphMin,
      });
      setYAxisVal({
        max: graphMax,
        min: graphMin,
      });
      setZAxisVal({
        max: graphMax,
        min: graphMin,
      });
    } else {
      setXAxisVal({});
      setYAxisVal({});
      setZAxisVal({});
      setImmutabel(true);
    }
  };

  const singleReport = () => {
    window.open(
      `/report?did=${deviceId}&str_time=${strTimeUTC}&end_time=${endTimeUTC}&din=true`,
      "Popup",
      "toolbar=no, location=no, statusbar=no, menubar=no, scrollbars=1, resizable=0, width=1000, height=600, top=30"
    );
  };

  let timeButton: any;
  if (timeLevel === "Relative Time") {
    timeButton = (
      <>
        <Button
          icon="pi pi-clock"
          className="p-button-info visualization-button ml-5 py-1 my-3"
          label={t("relative_time")}
          onClick={selectTime}
          disabled={disabled}
        />
      </>
    );
  } else if (timeLevel === "Absolute Time") {
    timeButton = (
      <>
        <Button
          icon="pi pi-clock"
          className="p-button-info visualization-button ml-5 py-1 my-3"
          label={t("absolute_time")}
          onClick={selectTime}
          disabled={disabled}
        />
      </>
    );
  }

  useEffect(() => {
    // setIsLoading(true);
    i18n.changeLanguage(localStorage.getItem("lang")!);
    const timeoffset: any = getTimeZoneName();
    setTimeOffset(timeoffset.split("+"));
    let freq_X: any, freq_Y: any, freq_Z: any;
    setPropsValue({
      station: props.data["station"],
      start_time_utc: props.data["start_time_utc"],
      end_time_utc: props.data["end_time_utc"],
    });
    setxConfig({
      title: {
        text: "",
      },
    });
    setyConfig({
      title: {
        text: "",
      },
    });
    setzConfig({
      title: {
        text: `${t("relative_timein")} [s]`,
      },
    });
    projectService
      .getVisualizationData(
        props.data["station"],
        props.data["start_time_utc"],
        props.data["end_time_utc"]
      )
      .then((response) => {
        let x_data: any;
        let y_data: any;
        let z_data: any;
        /**  FOR TIME SECONDS **/
        response.map((data: any) => {
          if (data["channel_X"]) {
            setFileName(data.filename);
            setRowId(data.rowId);
            setDeviceName(data.header_data["device_name"]);
            const date = calculateDate(
              new Date(data.start_time).getTime() / 1000
            );
            const eddate = calculateDate(
              new Date(data.end_time).getTime() / 1000
            );
            setStrtTime(date);
            setEndTime(eddate);
            setStrtTimeUTC(Date.parse(data.start_time) / 1000);
            setEndTimeUTC(Date.parse(data.end_time) / 1000);
            setDeviceId(data.did);

            const pos: any = getDecimalValue(data.gps_pos);
            setGpsPos(pos);
            setXAxisUnit(data.unit);
            const maxX: any = configureDecimal(data.max, 3);
            setMaxX(maxX);
            if (data.frequence_X === null) {
              freq_X = "";
            } else {
              freq_X = configureDecimal(data.frequence_X, 2);
            }
            setFrequencyX(freq_X);
            setTriggerX(parseFloat(data.trigger_level));
            x_data = data.channel_X.map((x: any, i: any) => [
              +((data.time_interval / data.channel_X.length) * i).toFixed(3),
              x,
            ]);
          } else if (data["channel_Y"]) {
            setYAxisUnit(data.unit);
            const maxY: any = configureDecimal(data.max, 3);
            setMaxY(maxY);
            if (data.frequence_Y === null) {
              freq_Y = "";
            } else {
              freq_Y = configureDecimal(data.frequence_Y, 2);
            }
            setFrequencyY(freq_Y);
            setTriggerY(parseFloat(data.trigger_level));
            y_data = data.channel_Y.map((x: any, i: any) => [
              +((data.time_interval / data.channel_Y.length) * i).toFixed(3),
              x,
            ]);
          } else if (data["channel_Z"]) {
            setZAxisUnit(data.unit);
            const maxZ: any = configureDecimal(data.max, 3);
            setMaxZ(maxZ);
            if (data.frequence_Z === null) {
              freq_Z = "";
            } else {
              freq_Z = configureDecimal(data.frequence_Z, 2);
            }
            setFrequencyZ(freq_Z);
            setTriggerZ(parseFloat(data.trigger_level));
            z_data = data.channel_Z.map((x: any, i: any) => [
              +((data.time_interval / data.channel_Z.length) * i).toFixed(3),
              x,
            ]);
          }
        });

        setXdata(x_data);
        setYdata(y_data);
        setZdata(z_data);
        setRelativeXData(x_data);
        setRelativeYData(y_data);
        setRelativeZData(z_data);
      });
  }, [i18n.language]);

  useEffect(() => {
    setIsLoading(true);
    ["mousemove", "touchmove", "touchstart"].forEach(function (eventType) {
      document
        ?.getElementById("hightchart-wrapper")
        ?.addEventListener(eventType, function (e: any) {
          let chart, point, i, event: any;

          for (i = 0; i < Highcharts.charts.length; i = i + 1) {
            chart = Highcharts.charts[i];
            if (chart) {
              // Find coordinates within the chart
              event = chart.pointer.normalize(e);
              // Get the hovered point
              chart.series.forEach((series: any) => {
                const point = series.searchPoint(event, true);
                if (point) {
                  try {
                    point.highlight(e);
                  } catch (err) {
                    // pass;
                  }
                }
              });
            }
          }
        });
    });
    setIsLoading(false);
    return;
  }, [componentRendered]);

  return (
    <>
      <Card
        // title={t("visualization")}
        style={{
          margin: "2em auto",
          overflowX: "auto",
          height: !isLoading ? "auto" : "400px",
        }}
      >
        <div className="flex flex-wrap align-items-center">
          <h4>
            {t("visualization_for")} {props.data["station_name"]} @ {strtTime} [
            {getTimeZoneName()}]
          </h4>
          <div
            className="flex flex-wrap align-items-center"
            style={{ marginLeft: "auto" }}
          >
            <Button
              icon="pi pi-chart-line"
              className="p-button-raised p-button-text ml-2 py-1 my-3"
              label={t("trace")}
              onClick={() => {
                setDisabled(false);
                setTrace(true);
                setNorm(false);
                setFFT(false);
                setVectorSum(false);
                setTraces("Trace");
                setComponentRendered(componentRendered + 1);
              }}
              style={{
                background: trace ? "#facccc" : "",
                color: trace ? "white !important" : "",
              }}
            />
            <Button
              icon="pi pi-chart-line"
              className="p-button-raised p-button-text ml-2 py-1 my-3"
              label="FFT"
              onClick={fftSelect}
              style={{ background: fft ? "#facccc" : "" }}
            />
            <Button
              icon="pi pi-chart-line"
              className="p-button-raised p-button-text ml-2 py-1 my-3"
              label="KB"
              style={{ cursor: "no-drop" }}
            />
            <Button
              icon="pi pi-chart-line"
              className="p-button-raised p-button-text ml-2 py-1 my-3"
              label={t("norm")}
              onClick={normSelect}
              style={{ background: norm ? "#facccc" : "" }}
            />
            {timeButton}
            <Tooltip id="trigger-level" style={{ zIndex: "10000" }} />
            <img
              alt="Trigger"
              src={
                triggerSelected
                  ? "/images/trigger-level.png"
                  : "/images/trigger-level(selected).png"
              }
              height="30"
              className="p-button-raised p-button-text ml-5 trigger-level-img my-3"
              onClick={triggerApply}
              style={{
                pointerEvents: disabled ? "none" : "auto",
                cursor: disabled ? "not-drop" : "pointer",
              }}
              data-tooltip-id={"trigger-level"}
              data-tooltip-content={t("trigger_level")}
            />
            <Tooltip id="vector-sum" style={{ zIndex: "10000" }} />
            <img
              src={
                vectorSum
                  ? "/images/vector-sum(selected).png"
                  : "/images/vector-sum.png"
              }
              alt="Vector Sum"
              height="30"
              className="ml-5 sum-img my-3"
              onClick={vectorSumSelect}
              style={{ cursor: "pointer" }}
              data-tooltip-id={"vector-sum"}
              data-tooltip-content={t("vector_sum")}
            />
            <BsFillMicFill className="microphone-icon  mx-2 my-3" />
            <SelectButton
              value={traces}
              options={options}
              onChange={(e) => selectTraceEvent(e)}
              className="my-3"
            />
            <Button
              icon="pi pi-file-pdf"
              className="p-button-primary device-edit-button my-3 ml-2"
              tooltip={t("download_report")}
              onClick={singleReport}
            />
            <Button
              icon="pi pi-download"
              className="p-button-info download-seg2-button my-3 ml-2"
              tooltip={t("download_zip")}
              onClick={downloadSingleSeg2}
            />
            <Tooltip id="previous-event" />
            <i
              className="pi pi-chevron-left pt-2 my-3"
              onClick={onPreviousSelect}
              style={{
                cursor: leftDisabled ? "not-allowed" : "pointer",
              }}
              data-tooltip-id={"previous-event"}
              data-tooltip-content={t("previous_event")}
            ></i>
            <Tooltip id="next-event" />
            <i
              className="pi pi-chevron-right pt-2 my-3"
              onClick={onNextSelect}
              style={{
                cursor: rightDisabled ? "not-allowed" : "pointer",
              }}
              data-tooltip-id={"next-event"}
              data-tooltip-content={t("next_event")}
            ></i>{" "}
          </div>
        </div>

        <div className="grid align-item-center justify-content-around m-0">
          {!isLoading ? (
            <>
              <div className="sm:col-12 lg:col-9">
                {trace ? (
                  <div id="hightchart-wrapper">
                    <HighchartsReact
                      immutable={immutable}
                      ref={chart1Ref}
                      highcharts={Highcharts}
                      options={graph_x}
                    />
                    <HighchartsReact
                      immutable={immutable}
                      ref={chart2Ref}
                      highcharts={Highcharts}
                      options={graph_y}
                    />
                    <HighchartsReact
                      immutable={immutable}
                      ref={chart3Ref}
                      highcharts={Highcharts}
                      options={graph_z}
                    />
                  </div>
                ) : (
                  <></>
                )}

                {vectorSum ? (
                  <HighchartsReact
                    immutable={immutable}
                    highcharts={Highcharts}
                    options={vector_sum_graph}
                  />
                ) : (
                  <></>
                )}
                {fft ? (
                  <>
                    <div id="hightchart-wrapper">
                      <HighchartsReact
                        immutable={immutable}
                        ref={chart1Ref}
                        highcharts={Highcharts}
                        options={fftGraph_x}
                      />
                      <HighchartsReact
                        immutable={immutable}
                        ref={chart2Ref}
                        highcharts={Highcharts}
                        options={fftGraph_y}
                      />
                      <HighchartsReact
                        immutable={immutable}
                        ref={chart3Ref}
                        highcharts={Highcharts}
                        options={fftGraph_z}
                      />
                    </div>
                  </>
                ) : (
                  <></>
                )}
                {norm ? (
                  <HighchartsReact
                    immutable={immutable}
                    highcharts={Highcharts}
                    options={scatter_graph}
                  />
                ) : (
                  <></>
                )}
              </div>
              <div className="sm:col-12 lg:col-3">
                <Card title={t("view_details")} style={{ overflowX: "auto" }}>
                  <h3>{t("project_name")} : </h3>
                  <h4>
                    <span className="device-detail-value">
                      {props.data["project"]}
                    </span>
                  </h4>
                  <h3>{t("station_name")} : </h3>
                  <h4>
                    <span className="device-detail-value">
                      {props.data["station_name"]}
                    </span>
                  </h4>
                  <h3>
                    {t("device_id")} :{" "}
                    <span className="device-detail-value">{deviceId}</span>{" "}
                  </h3>
                  <h3>
                    {t("gps_position")}:{" "}
                    <span className="device-detail-value">{gpsPos} </span>{" "}
                  </h3>

                  <h4>{t("max_amplitudes")}</h4>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">X-Axis:</span>
                    <span className="device-detail-axis-value">
                      {maxX} [{xAxisUnit}]
                    </span>
                  </div>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">Y-Axis:</span>
                    <span className="device-detail-axis-value">
                      {maxY} [{yAxisUnit}]
                    </span>
                  </div>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">Z-Axis:</span>
                    <span className="device-detail-axis-value">
                      {maxZ} [{zAxisUnit}]
                    </span>
                  </div>

                  <h4>{t("dom_frequencies")}</h4>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">X-Axis: </span>
                    <span className="device-detail-axis-value">
                      {" "}
                      {frequencyX} [Hz]{" "}
                    </span>
                  </div>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">Y-Axis: </span>
                    <span className="device-detail-axis-value">
                      {" "}
                      {frequencyY} [Hz]{" "}
                    </span>
                  </div>
                  <div className="device-detail-axis">
                    <span className="device-detail-value">Z-Axis: </span>
                    <span className="device-detail-axis-value">
                      {" "}
                      {frequencyZ} [Hz]{" "}
                    </span>
                  </div>
                </Card>
              </div>
            </>
          ) : (
            <Spinner />
          )}
        </div>
      </Card>
    </>
  );
};

export default Visualization;
